import type { Task } from '@medplum/fhirtypes';
import React from 'react';

import {
  CLINICAL_TASK_PRIORITY_LABELS,
  CLINICAL_TASK_STATUS_LABELS,
  asClinicalTaskStatus,
  categoryLabel,
  readTaskCategory,
  readTaskDueDate,
  readTaskPriority,
} from '@eluve/clinical-tasks';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Stack,
  Text,
} from '@eluve/components';
import { useFhirRestRead } from '@eluve/fhir-data';
import { FhirVersionHistory, formatFhirDate } from '@eluve/fhir-renderer';

/**
 * Deep-linkable side drawer for one clinician task (ENG-5373 for tasks):
 * the task's fields, the complete/undo action (same transition as the row
 * checkbox), and the `_history` audit trail (REST — the FHIR GraphQL spec
 * has no `_history`).
 */
export type TaskDetailDrawerProps = {
  task: Task | undefined;
  open: boolean;
  onClose: () => void;
  /** The complete/undo write is in flight for this task. */
  toggling: boolean;
  onToggle: (taskId: string, done: boolean) => void;
};

const FieldRow: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <Text inline size="sm" color="tertiary" className="shrink-0">
      {label}
    </Text>
    <Text inline size="sm" className="text-right privacy-text">
      {value ?? '—'}
    </Text>
  </div>
);

export const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
  task,
  open,
  onClose,
  toggling,
  onToggle,
}) => {
  const taskId = task?.id;
  const taskVersion = task?.meta?.versionId;
  const history = useFhirRestRead(
    taskId ? (medplum) => medplum.readHistory('Task', taskId) : null,
    [taskId, taskVersion],
  );
  const versions: Task[] = (history.data?.entry ?? []).flatMap((entry) =>
    entry.resource ? [entry.resource] : [],
  );

  const done = task?.status === 'completed';
  const statusLabel = task
    ? CLINICAL_TASK_STATUS_LABELS[
        asClinicalTaskStatus(task.status) ?? 'requested'
      ]
    : null;
  const dueDate = task ? readTaskDueDate(task) : null;
  const priority = task ? readTaskPriority(task) : null;
  const category = task ? readTaskCategory(task) : null;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-[min(640px,94vw)] gap-0 overflow-y-auto sm:max-w-none"
        data-testid="task-detail-drawer"
      >
        {task && (
          <>
            <SheetHeader>
              <SheetTitle className="privacy-text">
                {task.description ?? '(no description)'}
              </SheetTitle>
              <SheetDescription className="privacy-text">
                {[
                  statusLabel,
                  dueDate ? `Due ${formatFhirDate(dueDate)}` : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </SheetDescription>
            </SheetHeader>

            <Stack gap={6} align="stretch" className="px-4 pb-6">
              <div>
                <FieldRow label="Status" value={statusLabel} />
                <FieldRow
                  label="Priority"
                  value={
                    priority ? CLINICAL_TASK_PRIORITY_LABELS[priority] : null
                  }
                />
                <FieldRow
                  label="Category"
                  value={category ? categoryLabel(category) : null}
                />
                <FieldRow
                  label="Due date"
                  value={dueDate ? (formatFhirDate(dueDate) ?? null) : null}
                />
                <FieldRow
                  label="Assignee"
                  value={task.owner?.display ?? null}
                />
                <FieldRow
                  label="Description"
                  value={task.description ?? null}
                />
              </div>

              <div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={toggling || !taskId}
                  onClick={() => taskId && onToggle(taskId, !done)}
                  data-testid="task-drawer-toggle"
                >
                  {done ? 'Reopen task' : 'Mark as done'}
                </Button>
              </div>

              <div>
                <Text
                  size="xs"
                  color="tertiary"
                  weight="medium"
                  className="mb-2 tracking-wide uppercase"
                >
                  Change log & audit trail
                </Text>
                {history.loading ? (
                  <Text size="sm" color="tertiary" weight="regular">
                    Loading history…
                  </Text>
                ) : (
                  <FhirVersionHistory versions={versions} />
                )}
              </div>
            </Stack>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
